import React, { useState, useEffect, useRef } from "react";
import { Input } from "@coreai/component-library";
import { useSearchParams  } from "react-router-dom";
import { useTheme } from "@emotion/react";
import {
  listProjects,
  ProjectsModel,
} from "../../../services/projectsService";
import { useLanguage } from "LanguageContext";

interface ProjectTextFieldProps {
  setSelectProject: (project: ProjectsModel | null) => void;
  setSearchProject:(searchProj: string | null) => void;
  showProjectName?: boolean;
}

export default function ProjectsSearch(props: Readonly<ProjectTextFieldProps>) {
  const { t } = useLanguage();
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");

  const [chosenProject, setChosenProject] = useState<ProjectsModel | null>(
    null
  );
  const [projects, setProjects] = useState<ProjectsModel[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ProjectsModel[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  const projectSearch: string | null = searchParams.get("project");
  const isLoadProjectRef = useRef<Boolean>(false);

  useEffect(() => {
    async function fetchData() {
      const projectsList = await listProjects({ sortingColumns: {} });
      if (!projectsList?.results) return;
      isLoadProjectRef.current = true;

      setProjects((prevSuggestions) => {
        const newSuggestions = projectsList?.results?.filter(
          (result: any) =>
            !prevSuggestions.some(
              (suggestion) => suggestion.name === result.name
            )
        );

        return [
          ...prevSuggestions,
          ...newSuggestions.map((result: any) => ({
            advertiser: result.advertiser,
            id: result.id,
            name: result.name,
          })),
        ];
      });
    }
    !isLoadProjectRef.current && fetchData();
  }, []);

   const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
        props.setSearchProject(inputValue);
        setShowDropdown(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  setChosenProject(null);
  setInputValue(value);

  if (value) {
    const newFilteredProjects: ProjectsModel[] = [];
    let projSearch = "";
    let advSearch = "";

    if (value.includes("-")) {
      [projSearch, advSearch] = value.split("-");
      for (const project of projects) {
        if (
          project.advertiser.toLowerCase().includes(advSearch.toLowerCase()) &&
          (!props.showProjectName || project.name.toLowerCase().includes(projSearch.toLowerCase()))
        ) {
          newFilteredProjects.push(project);
        }
      }
    } else {
      for (const project of projects) {
        if (
          project.advertiser.toLowerCase().includes(value.toLowerCase()) ||
          (props.showProjectName &&
            project.name.toLowerCase().includes(value.toLowerCase()))
        ) {
          newFilteredProjects.push(project);
        }
      }
    }
    setFilteredProjects(newFilteredProjects);
  } else {
    setFilteredProjects(projects);
  }

  setShowDropdown(true);
};


  const handleFocus = () => {
    if (!inputValue) {
      setFilteredProjects(projects);
    }
    setShowDropdown(true);
  };

  const handleSuggestionClick = (suggestion: ProjectsModel) => {
    setChosenProject(suggestion);
    setShowDropdown(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    props.setSelectProject(chosenProject);
  }, [chosenProject, props]);

  useEffect(() => {
    if(projects?.length && projectSearch){
        const autoSetProject = projects.find(
        (item) => `${item.name}-${item.advertiser}` === projectSearch
        );
        setChosenProject(autoSetProject || null);
        window.history.replaceState({}, '', '/analyses');
    }
  }, [projectSearch, projects]);

  const dropdownStyles = {
    position: "absolute",
    backgroundColor: "white",
    border: `1px solid ${theme.colors.strokeGray}`,
    borderTop: "none",
    borderRadius: `0 0 ${theme.radius.xs} ${theme.radius.xs}`,
    width: "100%",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    boxShadow: theme.shadows.medium,
  } as React.CSSProperties;

  const suggestionsContainerStyles = {
    maxHeight: "150px",
    overflowY: "auto",
  } as React.CSSProperties;

  const dropdownItemStyles = {
    padding: theme.spacing.md,
    fontFamily: theme.typography.bodyMedium.fontFamily,
    fontSize: theme.typography.bodyMedium.fontSize,
    fontWeight: theme.typography.bodyMedium.fontWeight,
    lineHeight: theme.typography.bodyMedium.lineHeight,
    color: theme.colors.textMedium,
    cursor: "pointer",
  } as React.CSSProperties;

  const dropdownItemHoverStyles = {
    backgroundColor: "#f0f0f0",
  };

  const containerStyles = {
    position: "relative",
    width: "100%",
  } as React.CSSProperties;
  return (
    <div style={containerStyles} ref={dropdownRef}>
      
      <Input
        type="text"
        label={
          props.showProjectName
            ? t("analyse_form_label_project_advertiser")
            : t("analyse_form_label_advertiser")
        }
        value={
          chosenProject
            ? props.showProjectName
              ? `${chosenProject?.name} - ${chosenProject?.advertiser}`
              : chosenProject?.advertiser
            : inputValue
        }
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        suffixIcon="search"
        autoComplete="off"
      />

      {showDropdown && (
        <div style={dropdownStyles}>
          <div style={suggestionsContainerStyles}>
            {filteredProjects
              .sort((a, b) => {
                if (a.name !== b.name) {
                  return a.name.localeCompare(b.name);
                }
                return a.advertiser.localeCompare(b.advertiser);
              })
              .map((suggestion) => (
                <div
                  key={suggestion.id}
                  style={dropdownItemStyles}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      dropdownItemHoverStyles.backgroundColor)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  {props.showProjectName
                    ? `${suggestion.name} - ${suggestion.advertiser}`
                    : suggestion.advertiser}
                </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
